import React, { forwardRef } from 'react';
import type { ReactNode } from 'react';

// Inherited viewport limits are not supported by @atlaskit/css's size types.
import { cssMap } from '@compiled/react';
import type { PopupComponentProps } from '@atlaskit/popup/types';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	popup: {
		display: 'flex',
		flexDirection: 'column',
		minHeight: 0,
		minWidth: 0,
		maxHeight: 'inherit',
		maxWidth: 'inherit',
		overflow: 'auto',
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
		borderRadius: token('radius.small'),
	},
	dialog: {
		width: '400px',
		maxWidth: '100%',
		maxHeight: 'inherit',
		minHeight: 0,
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		boxSizing: 'border-box',
		position: 'relative',
		paddingTop: token('space.250'),
		paddingRight: token('space.250'),
		paddingBottom: token('space.250'),
		paddingLeft: token('space.250'),
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small'),
	},
	compact: {
		width: '358px',
		paddingTop: token('space.200'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.150'),
		gap: token('space.100'),
		borderRadius: token('radius.xlarge'),
	},
	fixed: { flexShrink: 0 },
	body: {
		minHeight: 0,
		minWidth: 0,
		overflowY: 'auto',
		overflowX: 'auto',
	},
});

// Keep the viewport constraint on a flex parent so its children can shrink on
// both the Popper and top-layer implementations of Popup.
export const AnalyticsPopupContainer: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<PopupComponentProps> & React.RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, PopupComponentProps>(
	(
		{
			children,
			shouldFitViewport,
			shouldFitContainer,
			shouldRenderToParent,
			appearance,
			isReferenceHidden,
			xcss,
			...htmlAttributes
		},
		ref,
	) => (
		<div {...htmlAttributes} ref={ref} css={styles.popup}>
			{children}
		</div>
	),
);

type AnalyticsDialogLayoutProps = {
	header: ReactNode;
	footer: ReactNode;
	banner?: ReactNode;
	children: ReactNode;
	isCompactLayout: boolean;
	label: string;
	testId: string;
};

export const AnalyticsDialogLayout: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<AnalyticsDialogLayoutProps> & React.RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, AnalyticsDialogLayoutProps>(
	({ header, footer, banner, children, isCompactLayout, label, testId }, ref) => {
		return (
			<div
				ref={ref}
				role="dialog"
				aria-label={label}
				aria-modal="true"
				data-testid={testId}
				css={[styles.dialog, isCompactLayout && styles.compact]}
			>
				<div css={styles.fixed}>{header}</div>
				<div css={styles.body}>
					{children}
					{banner}
				</div>
				<div css={styles.fixed}>{footer}</div>
			</div>
		);
	},
);
